import * as React from "react";
import * as ReactQuery from "react-query";

export interface GenericDataSource_CapableOfBeingIntersected<
  DataConsumerInterface extends unknown
> {
  dataUpdatedAt: ReactQuery.UseQueryResult["dataUpdatedAt"];
  data: ReactQuery.UseQueryResult<DataConsumerInterface>["data"];

  errorUpdatedAt: ReactQuery.UseQueryResult["errorUpdatedAt"];
  error: ReactQuery.UseQueryResult<DataConsumerInterface>["error"];

  status: ReactQuery.UseQueryResult<DataConsumerInterface>["status"];

  ignoreMyCurrentState?: () => boolean;
}

export enum GenericDataSource_MostLatestType {
  none = "none",
  data = "data",
  error = "error",
}

export interface GenericDataSourceIntersectionConsumerInterface<
  DataConsumerInterface extends unknown
> {
  latest?: DataConsumerInterface;
  latestError?: unknown;

  mostLatestType: `${GenericDataSource_MostLatestType}`;
}

export const createDataSourceIntersectionContext = <
  DataConsumerInterface extends unknown
>(): [
  React.Context<
    GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>
  >,
  () => GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>
] => {
  const defaultValue =
    {} as unknown as GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>;

  const DataSourceIntersectionContext = React.createContext(defaultValue);

  const useDataSourceIntersection = () => {
    const context = React.useContext(DataSourceIntersectionContext);
    if (context === defaultValue) {
      throw new Error(
        "useDataSourceIntersection must be used within a GenericDataSourceIntersectionProvider"
      );
    }
    return context;
  };

  return [DataSourceIntersectionContext, useDataSourceIntersection];
};

export interface GenericDataSourceIntersectionProviderProps<
  DataConsumerInterface extends unknown
> {
  context: React.Context<
    GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>
  >;

  dataSources: GenericDataSource_CapableOfBeingIntersected<DataConsumerInterface>[];

  children?: React.ReactNode | undefined;
}
const compareDataSources = <DataConsumerInterface extends unknown>(
  incomingDataSource: GenericDataSource_CapableOfBeingIntersected<DataConsumerInterface>,
  latestDataSource?: GenericDataSource_CapableOfBeingIntersected<DataConsumerInterface>
) => {
  const latestIntersectionCapability = latestDataSource;
  const thisIntersectionCapability = incomingDataSource;

  const isThereACurrentLatest =
    !!latestIntersectionCapability &&
    !!latestIntersectionCapability.dataUpdatedAt &&
    latestIntersectionCapability.dataUpdatedAt >= 0;
  const currentLatestTimestamp =
    (latestIntersectionCapability && latestIntersectionCapability.dataUpdatedAt
      ? latestIntersectionCapability.dataUpdatedAt
      : null) ?? 0;

  const isThisDataSourceCapableOfBeingTheLatestAtAll =
    !!thisIntersectionCapability &&
    !!thisIntersectionCapability.dataUpdatedAt &&
    thisIntersectionCapability.dataUpdatedAt >= 0;
  const thisDataSourceTimestamp =
    (thisIntersectionCapability && thisIntersectionCapability.dataUpdatedAt
      ? thisIntersectionCapability.dataUpdatedAt
      : null) ?? 0;

  const isThisDataSourceOnIgnore = (
    thisIntersectionCapability.ignoreMyCurrentState || (() => false)
  )();

  const isThisIncomingDataSourceTheLatestUpdate = !!(
    (!isThereACurrentLatest && isThisDataSourceCapableOfBeingTheLatestAtAll) ||
    (isThereACurrentLatest &&
      isThisDataSourceCapableOfBeingTheLatestAtAll &&
      currentLatestTimestamp < thisDataSourceTimestamp &&
      !isThisDataSourceOnIgnore)
  );

  // ---

  const isThereACurrentLatest_error = !!(
    !!latestIntersectionCapability &&
    !!latestIntersectionCapability.errorUpdatedAt &&
    latestIntersectionCapability.errorUpdatedAt >= 0
  );
  const currentLatestTimestamp_error =
    (latestIntersectionCapability && latestIntersectionCapability.errorUpdatedAt
      ? latestIntersectionCapability.errorUpdatedAt
      : undefined) || 0;

  const isThisDataSourceCapableOfBeingTheLatestAtAll_error = !!(
    !!thisIntersectionCapability &&
    !!thisIntersectionCapability.errorUpdatedAt &&
    thisIntersectionCapability.errorUpdatedAt >= 0
  );
  const thisDataSourceTimestamp_error =
    (thisIntersectionCapability && thisIntersectionCapability.errorUpdatedAt
      ? thisIntersectionCapability.errorUpdatedAt
      : null) ?? 0;

  const isThisIncomingDataSourceTheLatestUpdate_error = !!(
    (!isThereACurrentLatest_error &&
      isThisDataSourceCapableOfBeingTheLatestAtAll_error) ||
    (isThereACurrentLatest_error &&
      isThisDataSourceCapableOfBeingTheLatestAtAll_error &&
      currentLatestTimestamp_error < thisDataSourceTimestamp_error)
  );

  // ---

  const isThisTheMostLatestType = !!(
    isThisIncomingDataSourceTheLatestUpdate ||
    (!isThisDataSourceOnIgnore && isThisIncomingDataSourceTheLatestUpdate_error)
  );

  return {
    isThisIncomingDataSourceTheLatestUpdate,
    isThisIncomingDataSourceTheLatestUpdate_error,
    isThisTheMostLatestType,
  };
};
export const GenericDataSourceIntersectionProvider = <
  DataConsumerInterface extends unknown
>(
  props: GenericDataSourceIntersectionProviderProps<DataConsumerInterface>
): React.ReactElement<any, any> => {
  type thisDataSourceType =
    GenericDataSourceIntersectionProviderProps<DataConsumerInterface>["dataSources"][0];

  const [latestCache, setLatestCache] =
    React.useState<thisDataSourceType | null>(null);

  const dataSources = React.useMemo(() => {
    return [...props.dataSources, ...(latestCache ? [latestCache] : [])];
  }, [props.dataSources, latestCache]);

  React.useEffect(() => {
    const initialLatest = undefined as unknown as thisDataSourceType;
    const latestReduced = dataSources.reduce((latest, dataSource) => {
      const comparison = compareDataSources(dataSource, latest);
      if (comparison.isThisIncomingDataSourceTheLatestUpdate) {
        return dataSource;
      }
      return latest;
    }, initialLatest);

    const acceptableCacheReadyStates: thisDataSourceType["status"][] = [
      "success",
    ];
    if (
      latestReduced &&
      acceptableCacheReadyStates.includes(latestReduced.status) &&
      latestReduced !== latestCache
    ) {
      setLatestCache(latestReduced);
    }
  }, [dataSources, latestCache]);

  const latest: GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>["latest"] =
    React.useMemo(() => {
      const latest = !!latestCache
        ? (latestCache.data as DataConsumerInterface)
        : undefined;
      return latest;
    }, [latestCache]);

  const latestError: GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>["latestError"] =
    React.useMemo(() => {
      const initialLatest = undefined as unknown as thisDataSourceType;
      const latestReduced = dataSources.reduce((latest, dataSource) => {
        const comparison = compareDataSources(dataSource, latest);
        if (comparison.isThisIncomingDataSourceTheLatestUpdate_error) {
          return dataSource;
        }
        return latest;
      }, initialLatest);

      const latestError = !!latestReduced ? latestReduced.error : undefined;

      return latestError;
    }, [dataSources]);

  const mostLatestType: GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface>["mostLatestType"] =
    React.useMemo(() => {
      const initialLatest = undefined as unknown as thisDataSourceType;
      const latestReduced = dataSources.reduce((latest, dataSource) => {
        const comparison = compareDataSources(dataSource, latest);
        if (comparison.isThisTheMostLatestType) {
          return dataSource;
        }
        return latest;
      }, initialLatest);

      let mostLatestType = GenericDataSource_MostLatestType.none;
      if (!!latestReduced) {
        const isTheDataFresherThanTheError =
          (latestReduced.dataUpdatedAt || 0) >
          (latestReduced.errorUpdatedAt || 0);
        mostLatestType = isTheDataFresherThanTheError
          ? GenericDataSource_MostLatestType.data
          : GenericDataSource_MostLatestType.error;
      }

      return mostLatestType;
    }, [dataSources]);

  const value: GenericDataSourceIntersectionConsumerInterface<DataConsumerInterface> =
    React.useMemo(
      () => ({
        latest,
        latestError,
        mostLatestType,
      }),
      [latest, latestError, mostLatestType]
    );
  return (
    <props.context.Provider value={value}>
      {props.children || []}
    </props.context.Provider>
  );
};
